import { useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Link, type To } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  Info,
  type LucideIcon,
} from 'lucide-react';
import SafaqatLogo from '../brand/SafaqatLogo';
import Input from '../base/Input';

/**
 * The frame for the standalone auth pages (/signin, /reset-password). THEME.md:
 * a white page, one white card with a hairline border and the small brand
 * shadow, and the full logo lockup. No glass, tilt, light beams, glow or
 * background blobs; those broke THEME §5 and the blobs never even rendered.
 *
 * Proportion rule (THEME): text 0.875rem sits with a 1rem icon, 0.9375rem with
 * 1.125rem, 1rem with 1.25rem; every tap target is at least 2.75rem.
 */

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40] focus-visible:ring-offset-2 ' +
  'dark:focus-visible:ring-[#7FD6C3] dark:focus-visible:ring-offset-[var(--pv-color-surface-strong)]';

/** A link inside a sentence ("No account? Sign up free"). */
export const authLinkClass = `rounded-md font-bold text-[var(--pv-color-primary)] underline-offset-4 transition-colors hover:underline ${FOCUS_RING}`;

/** A text button or link that stands on its own line (forgot password, back to sign in). */
export const authActionClass = `inline-flex min-h-[2.75rem] items-center gap-2 rounded-lg text-[0.9375rem] underline-offset-4 transition-colors hover:underline disabled:cursor-not-allowed disabled:opacity-60 ${FOCUS_RING}`;

interface AuthShellProps {
  /** The card heading, the page's <h1>. */
  title: string;
  subtitle?: string;
  /** The back link above the card, at the inline start. */
  backTo: To;
  backLabel: string;
  children: ReactNode;
}

export default function AuthShell({ title, subtitle, backTo, backLabel, children }: AuthShellProps) {
  const { i18n } = useTranslation('common');
  const titleId = useId();

  return (
    <div dir={i18n.dir()} className="flex min-h-screen w-full flex-col bg-[var(--pv-color-surface)]">
      <div className="px-4 pt-4 sm:px-6 sm:pt-6">
        <Link
          to={backTo}
          className={`-ms-2 inline-flex min-h-[2.75rem] items-center gap-2 rounded-lg px-2 text-[0.9375rem] text-[var(--pv-color-text-muted)] transition-colors hover:text-[var(--pv-color-primary)] ${FOCUS_RING}`}
        >
          {/* Points back: left in English, mirrored to the right in Arabic. */}
          <ArrowLeft className="h-[1.125rem] w-[1.125rem] shrink-0 rtl:-scale-x-100" aria-hidden="true" />
          {backLabel}
        </Link>
      </div>

      <main className="flex flex-1 items-start justify-center px-4 pb-12 pt-2 sm:items-center sm:pb-16">
        <section
          aria-labelledby={titleId}
          className="w-full max-w-md rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] p-6 shadow-brand-sm sm:p-8"
        >
          <header className="mb-6 text-center">
            <SafaqatLogo className="mx-auto block h-[5.5rem] w-auto" />
            <h1 id={titleId} className="mt-5 text-2xl font-bold leading-tight text-[var(--pv-color-primary)]">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-2 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)]">{subtitle}</p>
            ) : null}
          </header>
          {children}
        </section>
      </main>
    </div>
  );
}

type NoticeTone = 'info' | 'success' | 'warning' | 'error';

const NOTICE_STYLES: Record<NoticeTone, { box: string; icon: LucideIcon; iconClass: string }> = {
  info: {
    box: 'border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-text)]',
    icon: Info,
    iconClass: 'text-[var(--pv-color-primary)]',
  },
  // Success keeps the semantic emerald, visibly not the brand green (THEME §2.5).
  success: {
    box: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-100',
    icon: CheckCircle2,
    iconClass: 'text-emerald-700 dark:text-emerald-300',
  },
  warning: {
    box: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-300/30 dark:bg-amber-300/10 dark:text-amber-100',
    icon: AlertTriangle,
    iconClass: 'text-amber-700 dark:text-amber-300',
  },
  error: {
    box: 'border-brandred-200 bg-brandred-soft text-brandred-700 dark:border-[#E06B60]/40 dark:bg-[#E06B60]/10 dark:text-brandred-light',
    icon: AlertCircle,
    iconClass: '',
  },
};

/** A message box at the top of the card. Errors are announced at once (role="alert"). */
export function AuthNotice({ tone, children }: { tone: NoticeTone; children: ReactNode }) {
  const { box, icon: Icon, iconClass } = NOTICE_STYLES[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mb-5 flex items-start gap-2.5 rounded-xl border p-3.5 text-sm leading-relaxed ${box}`}
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconClass}`} aria-hidden="true" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

interface AuthFieldProps extends Omit<ComponentPropsWithoutRef<typeof Input>, 'id' | 'invalid' | 'startAdornment'> {
  id: string;
  /** The visible label above the field. */
  label: string;
  /** Leading icon, drawn at 1.25rem in the muted ink (5.6:1), teal while the field has focus. */
  icon: LucideIcon;
  /** Shown under the field in red, and wired to aria-invalid / aria-describedby. */
  error?: string;
  /** A standing hint under the field (password rules). */
  hint?: string;
}

/**
 * Label, field and messages in one block: a visible label, the design-system
 * Input at 3rem tall with 1rem text, and the hint and error tied to the field
 * for screen readers.
 */
export function AuthField({ id, label, icon: Icon, error, hint, className = '', ...inputProps }: AuthFieldProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [inputProps['aria-describedby'], hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-bold text-[var(--pv-color-text)]">
        {label}
      </label>
      <Input
        {...inputProps}
        id={id}
        invalid={Boolean(error)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        wrapperClassName="group"
        startAdornment={
          <Icon
            className="h-5 w-5 text-[var(--pv-color-text-muted)] transition-colors group-focus-within:text-[var(--pv-color-primary)]"
            aria-hidden="true"
          />
        }
        // 3rem tall, and 1rem text at every width to sit with the 1.25rem icon.
        // Input sets `sm:text-sm`, and Tailwind emits that AFTER `sm:text-base`,
        // so only the important form wins. No second, browser-drawn eye in Edge.
        className={`h-12 sm:!text-base [&::-ms-reveal]:hidden ${className}`.trim()}
      />
      {hint ? (
        <p id={hintId} className="mt-2 text-sm leading-relaxed text-[var(--pv-color-text-muted)]">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="mt-2 flex items-start gap-1.5 text-sm leading-relaxed text-brandred-700 dark:text-brandred-light">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}

interface PasswordToggleProps {
  /** Whether the password is currently shown as plain text. */
  visible: boolean;
  onToggle: () => void;
  /** The id of the password field it controls. */
  controls: string;
  /** A fixed name ("Show password"); the pressed state carries on/off. */
  label: string;
}

/**
 * The show/hide control for a password field, for AuthField's `endAdornment`.
 * A 2.75rem toggle button whose box fills the field's end padding exactly
 * (`-me-3` cancels the slot's `end-3`), so it never covers typed text.
 */
export function PasswordToggle({ visible, onToggle, controls, label }: PasswordToggleProps) {
  const Icon = visible ? EyeOff : Eye;
  return (
    <button
      type="button"
      onClick={onToggle}
      // Keep the caret in the field when the toggle is clicked with a pointer.
      onMouseDown={(event) => event.preventDefault()}
      aria-label={label}
      aria-pressed={visible}
      aria-controls={controls}
      className={
        '-me-3 flex h-11 w-11 items-center justify-center rounded-lg text-[var(--pv-color-text-muted)] transition-colors ' +
        'hover:text-[var(--pv-color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40] ' +
        'dark:focus-visible:ring-[#7FD6C3]'
      }
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}
